/**
 * Prompt-chip questions shown in the Focus Group chat empty state, per audience.
 *
 * This was an inline ternary chain in AskNaturalOrganicChat's JSX. It moved here so the
 * set can be checked exhaustively against the audience picker: an audience that is
 * selectable but has no entry silently showed the natural-organic fallback instead, which
 * is how the chocolate audience shipped asking about organic produce (fixed in f176068).
 * See starterQuestions.test.ts — adding a segment to the picker without adding it here
 * now fails the build.
 */
import { SEGMENT_IDS } from '../../config/segments';

/** Shown when a segment has no questions of its own. */
export const DEFAULT_STARTER_QUESTIONS: readonly string[] = [
  'What motivates natural/organic purchases?',
  'How do shoppers choose between brands?',
  'What are common barriers to buying organic?',
];

export const STARTER_QUESTIONS: Record<number, readonly string[]> = {
  [SEGMENT_IDS.SMARTYPANTS]: [
    'What motivates vitamin and supplement purchases?',
    'How do consumers evaluate supplement brands?',
    'What are key factors when choosing gummy vitamins?',
  ],
  [SEGMENT_IDS.FLAVCITY]: [
    'What motivates protein product purchases?',
    'How do consumers evaluate protein brands?',
    'What is the perception of Flavcity products in the market?',
  ],
  [SEGMENT_IDS.ALT_MILK]: [
    'What motivates milk alternative product purchases?',
    'How do consumers evaluate milk alternative brands?',
    'what is the perception of dairy alternative products in the market?',
  ],
  [SEGMENT_IDS.DOGS_AUDIENCE]: [
    'What motivates dog food product purchases?',
    'How do consumers evaluate dog food brands?',
    'what is the perception of dog food products in the market?',
  ],
  [SEGMENT_IDS.CATS_AUDIENCE]: [
    'What motivates cat food product purchases?',
    'How do consumers evaluate cat food brands?',
    'what is the perception of cat food products in the market?',
  ],
  // Brand cohort: every twin already buys Freshpet as their primary brand, so "which brand"
  // questions are tautologies here. These probe why it won, what it competes with in the
  // same household, and what would lose it.
  [SEGMENT_IDS.FRESHPET_DOGS]: [
    'Why did these owners make Freshpet their main food?',
    'What else do they feed alongside Freshpet, and when?',
    'What would make them switch away from Freshpet?',
  ],
  [SEGMENT_IDS.AMY_AUDIENCE]: [
    'What motivates frozen meal product purchases?',
    'How do consumers evaluate frozen meal brands?',
    'what is the perception of frozen meal products in the market?',
  ],
  [SEGMENT_IDS.JERKY]: [
    'What motivates jerky product purchases?',
    'How do consumers evaluate jerky brands?',
    'what is the perception of jerky products in the market?',
  ],
  [SEGMENT_IDS.BREAKFAST_YOGURT]: [
    'What motivates yogurt purchases?',
    'How do consumers evaluate yogurt brands?',
    'what is the perception of yogurt products in the market?',
  ],
  [SEGMENT_IDS.MONDELEZ]: [
    'What motivates cookie product purchases?',
    'How do consumers evaluate cookie brands?',
    'what is the perception of cookie products in the market?',
  ],
  [SEGMENT_IDS.CHOCOLATE]: [
    'What motivates chocolate and cookie purchases?',
    'How do consumers decide between chocolate and cookie brands?',
    'When does chocolate win over a cookie, and when does it lose?',
  ],
  [SEGMENT_IDS.NATURAL_ORGANIC]: DEFAULT_STARTER_QUESTIONS,
  [SEGMENT_IDS.KNF_SURVEY]: [
    'What motivates bone broth purchases?',
    'How do consumers evaluate bone broth brands?',
    'what is the perception of bone broth products in the market?',
  ],
};

export const starterQuestionsFor = (segmentId: number): readonly string[] =>
  STARTER_QUESTIONS[segmentId] ?? DEFAULT_STARTER_QUESTIONS;
